import { Check, Package, User, Users } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { POST_BLACK, POST_MIN_CONTRAST, POST_WHITE, contrastRatio, type JobPostDesign, type JobPostWords } from '@nest-recruit/shared';
import styles from './PostPreview.module.css';

/** What the right-hand picture will be, once Auto is decided by the job. */
export type PreviewPicture = 'person' | 'objects' | 'none' | 'own_photo';

/** The colour words take on a filled shape: the background when it reads, else white or black. */
function onColour(fill: string, background: string): string {
  if (contrastRatio(background, fill) >= POST_MIN_CONTRAST) return background;
  return contrastRatio(POST_WHITE, fill) >= contrastRatio(POST_BLACK, fill) ? POST_WHITE : POST_BLACK;
}

/**
 * ADR 0066 — the free live preview: the post drawn by the app, not the AI, so colours, fonts and words can be judged
 * before paying. Approximate on purpose: the AI draws the real picture and lettering.
 */
export function PostPreview({
  words,
  design,
  company,
  picture,
  photoUrl,
}: {
  readonly words: JobPostWords;
  readonly design: JobPostDesign;
  readonly company: string;
  readonly picture: PreviewPicture;
  readonly photoUrl: string | null;
}): ReactNode {
  const { palette } = design;
  // The palette is the person's data, not a design token, so it reaches the stylesheet as custom properties.
  const colours = {
    '--post-text': palette.text,
    '--post-background': palette.background,
    '--post-primary': palette.primary,
    '--post-on-primary': onColour(palette.primary, palette.background),
    '--post-secondary': palette.secondary,
    '--post-accent': palette.accent,
  } as CSSProperties;
  const [first = '', ...rest] = words.headline.split(' ');
  const PictureIcon = design.picture.gender === 'group' ? Users : User;
  return (
    <div className={styles.frame} style={colours} data-headline={design.headlineFont} data-body={design.bodyFont} role="img" aria-label={`Preview of the post: ${words.headline}, ${words.role}`}>
      <span className={styles.logo}>LOGO</span>
      <span className={styles.company}>{company}</span>
      <span className={styles.lineA} aria-hidden />
      <span className={styles.lineB} aria-hidden />
      <span className={styles.dots} aria-hidden />

      <div className={styles.art} aria-hidden>
        {picture !== 'none' && <span className={styles.circle} />}
        {picture === 'none' && (
          <>
            <span className={styles.arch} />
            <span className={styles.block} />
          </>
        )}
        {picture === 'person' && <PictureIcon className={styles.figure} strokeWidth={1.25} />}
        {picture === 'objects' && <Package className={styles.figure} strokeWidth={1.25} />}
        {picture === 'own_photo' && photoUrl && <img className={styles.photo} src={photoUrl} alt="" />}
      </div>

      <div className={styles.copy}>
        <span className={styles.headline}>
          <span>{first}</span>
          {rest.length > 0 && <span className={styles.headlineBig}>{rest.join(' ')}</span>}
        </span>
        <span className={styles.underline} aria-hidden />
        <span className={styles.pill}>{words.role}</span>
        {words.facts && <span className={styles.facts}>{words.facts}</span>}
        {words.points.length > 0 && (
          <ul className={styles.points}>
            {words.points.map((point) => (
              <li key={point}>
                <span className={styles.tick} aria-hidden>
                  <Check strokeWidth={3} />
                </span>
                {point}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={styles.footer}>
        {words.button && <span className={styles.button}>{words.button}</span>}
        <span className={styles.apply}>
          {words.applyBy && <strong>{words.applyBy}</strong>}
          {words.applyAt && <span>{words.applyAt}</span>}
        </span>
      </div>
    </div>
  );
}
